import React, { useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import { SET_SUMMARIES } from './setSummaryData';
import { SET_REGISTRY } from './setRegistry';
import SuggestEditModal from './SuggestEditModal';
import { exportSetSummaryXlsx } from './lib/setSummaryExportRun';
import MarkWhatIOwnButton from './components/MarkWhatIOwnButton';
import { useFeatureFlag } from './featureFlags';

// --- Color tokens (mirror brand guide) ---
const C = {
  gold: 'var(--gold)',
  cyan: 'var(--cyan)',
  green: 'var(--green)',
  purple: 'var(--purple)',
  red: 'var(--red-2)',
  ink: 'var(--text-bright)',
  inkSoft: 'var(--muted-8)',
  inkFaint: '#5a6680',
  uiBg: 'var(--bg)',
  cardBg: 'var(--panel)',
  grid: 'rgba(var(--slate-rgb),0.18)',
  border: 'rgba(var(--slate-rgb),0.45)',
};

const STAT_COLOR = { gold: C.gold, cyan: C.cyan, green: C.green, purple: C.purple, red: C.red };
const TIER_COLOR = { normal: C.ink, rare: C.purple, mythic: C.gold };

// All responsive media queries live here. Inline styles can't do @media,
// so we inject a small scoped stylesheet at the top of the component.
const RESPONSIVE_CSS = `
.ss-page {
  min-height: 100vh;
  background:
    radial-gradient(ellipse 60% 40% at 80% 4%, rgba(var(--cyan-rgb),0.05) 0%, transparent 60%),
    radial-gradient(ellipse 70% 50% at 12% 95%, rgba(var(--gold-rgb),0.04) 0%, transparent 60%),
    ${C.uiBg};
  font-family: 'Rajdhani', sans-serif;
  color: ${C.ink};
  -webkit-font-smoothing: antialiased;
}
.ss-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px 80px;
}
.ss-stats {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}
.ss-twocol {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 16px;
}
.ss-rainbow {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}
.ss-chip {
  height: 78px;
  border-radius: 3px;
  padding: 7px 8px 6px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  position: relative;
}
/* Reserve space at the top-right of every section header + hero
   so the fixed NavDrawer hamburger never crashes into our text. */
.ss-clear-nav {
  padding-right: 56px;
}
.ss-artists {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 24px;
}
.ss-subset-row {
  display: grid;
  grid-template-columns: 64px 1fr 110px;
  gap: 14px;
  align-items: center;
}
.ss-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 18px;
  row-gap: 0;
}
.ss-photo-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 12px 10px;
}
.ss-cta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.ss-cardtoggle:hover {
  background: rgba(var(--cyan-rgb),0.08) !important;
  border-color: ${C.cyan} !important;
  color: ${C.cyan} !important;
}
/* Photo-gallery toggles get the gold treatment so collectors spot them. */
.ss-cardtoggle-gold:hover {
  background: rgba(var(--gold-rgb),0.12) !important;
}
@media (max-width: 900px) {
  .ss-stats { grid-template-columns: repeat(3, 1fr); }
  .ss-twocol { grid-template-columns: 1fr; }
  .ss-rainbow { grid-template-columns: repeat(4, 1fr); gap: 5px; }
  .ss-chip { height: 70px; }
  .ss-artists { grid-template-columns: 1fr; }
  .ss-cards-grid { grid-template-columns: repeat(2, 1fr); }
  .ss-photo-grid { grid-template-columns: repeat(6, 1fr); }
  .ss-cta-grid { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .ss-wrap { padding: 24px 14px 60px; }
  .ss-stats { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .ss-rainbow { grid-template-columns: repeat(3, 1fr); }
  .ss-chip { height: 64px; padding: 6px 7px 5px; }
  .ss-subset-row { grid-template-columns: 52px 1fr; row-gap: 4px; }
  .ss-subset-row > .ss-paracount { grid-column: 1 / -1; justify-self: start; }
  .ss-cards-grid { grid-template-columns: 1fr; }
  .ss-photo-grid { grid-template-columns: repeat(4, 1fr); }
  .ss-hero-title { font-size: 32px !important; }
}
@media (max-width: 380px) {
  .ss-rainbow { grid-template-columns: repeat(2, 1fr); }
}
/* CK 3.0 sticky "Mark what I own" bar. Desktop: pinned top (page content gets
   extra top padding so the first row is never covered on load; right padding
   clears the fixed NavDrawer hamburger). Mobile (<=900px): pinned bottom in
   thumb reach, with env(safe-area-inset-bottom) for the iOS home indicator.
   z-index sits above page content, below the NavDrawer (1050+) and modals. */
.ss-sticky-bar {
  position: fixed; left: 0; right: 0; top: 0; z-index: 900;
  background: var(--bg-sticky);
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  border-bottom: 1px solid ${C.border};
  box-shadow: 0 8px 30px rgba(0,0,0,0.35);
}
.ss-sticky-inner {
  max-width: 1200px; margin: 0 auto;
  display: flex; align-items: center; justify-content: center; gap: 20px;
  padding: 9px 72px 9px 20px;
}
.ss-has-bar .ss-wrap { padding-top: 96px; }
@media (max-width: 900px) {
  .ss-sticky-bar {
    top: auto; bottom: 0;
    border-bottom: none; border-top: 1px solid ${C.border};
    box-shadow: 0 -8px 30px rgba(0,0,0,0.35);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .ss-sticky-inner { justify-content: space-between; gap: 12px; padding: 9px 14px; }
  .ss-has-bar .ss-wrap { padding-top: 32px; padding-bottom: 150px; }
}
`;

// --- Transform Supabase row (snake_case) into the shape the rest of the
// component expects (camelCase, matching setSummaryData.js) ---
function rowToSummary(row) {
  return {
    setId:           row.set_id,
    year:            row.year,
    brand:           row.brand,
    title:           row.title,
    releaseDate:     row.release_date,
    lede:            row.lede,
    stats:           row.stats || [],
    hobbyBox:        row.hobby_box || [],
    pullRates:       row.pull_rates || [],
    parallelRainbow: row.parallel_rainbow || [],
    artists:         row.artists || [],
    subsets:         row.subsets || { base: [], insert: [], auto: [], sketch: [] },
  };
}

// --- Small presentational helpers ---

function SectionHeader({ tickColor, label, meta }) {
  return (
    <div className="ss-clear-nav" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: tickColor, flexShrink: 0 }} />
      <h2 style={{ fontFamily: "'Orbitron', monospace", fontSize: '15px', fontWeight: 700, letterSpacing: '3px', color: C.ink, margin: 0 }}>
        {label}
      </h2>
      <div style={{ flex: 1, minWidth: '20px', height: '1px', background: `linear-gradient(90deg, ${C.grid}, transparent)` }} />
      {meta && (
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', letterSpacing: '1px', color: C.inkFaint }}>
          {meta}
        </div>
      )}
    </div>
  );
}

function Stat({ stat }) {
  const accent = STAT_COLOR[stat.color] || C.ink;
  return (
    <div style={{
      background: `linear-gradient(180deg, rgba(var(--pcard-rgb),0.7), rgba(var(--pcard-rgb),0.4))`,
      border: `1px solid ${C.grid}`,
      borderTop: `2px solid ${accent}`,
      padding: '14px 14px 16px',
    }}>
      <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: 800, fontSize: '32px', lineHeight: 1, letterSpacing: '-0.5px', color: accent }}>
        {stat.num}
      </div>
      <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: 500, fontSize: '9px', letterSpacing: '2px', color: C.inkSoft, marginTop: '8px', textTransform: 'uppercase' }}>
        {stat.label}
      </div>
      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9.5px', color: C.inkFaint, marginTop: '3px', letterSpacing: '0.5px' }}>
        {stat.sub}
      </div>
    </div>
  );
}

function ParallelChip({ p }) {
  const textColor = p.darkText ? 'rgba(0,0,0,0.85)' : 'rgba(var(--wash-rgb),0.95)';
  const textShadow = p.darkText ? '0 1px 1px rgba(var(--wash-rgb),0.3)' : '0 1px 2px rgba(0,0,0,0.7)';
  return (
    <div className="ss-chip" style={{ background: p.gradient }}>
      <div style={{
        fontFamily: "'Orbitron', monospace",
        fontWeight: 700,
        fontSize: '8.5px',
        letterSpacing: '0.4px',
        lineHeight: 1.1,
        color: textColor,
        textShadow,
        wordBreak: 'break-word',
      }}>
        {p.name}
      </div>
      <div style={{
        fontFamily: "'Share Tech Mono', monospace",
        fontSize: '10.5px',
        color: textColor,
        textShadow,
        alignSelf: 'flex-end',
      }}>
        {p.pr}
      </div>
    </div>
  );
}

// One cell of the photo gallery. Missing photos (onError) collapse to a
// dashed placeholder so partially-shot sets still render an aligned grid.
function GalleryCell({ url, num, title, meta, onView }) {
  const [loaded, setLoaded] = useState(null); // null = pending, true, false
  return (
    <div>
      <div
        onClick={() => { if (loaded) onView(); }}
        style={{
          aspectRatio: '2.5 / 3.5',
          background: 'rgba(0,0,0,0.3)',
          border: loaded === false ? `1px dashed ${C.grid}` : `1px solid ${C.border}`,
          borderRadius: '3px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: loaded ? 'zoom-in' : 'default',
        }}>
        {loaded === false ? (
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', letterSpacing: '0.5px', color: C.inkFaint, textAlign: 'center', lineHeight: 1.6 }}>
            #{num}<br />PHOTO SOON
          </div>
        ) : (
          <img
            src={url}
            alt={`Card ${num} — ${title}`}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(false)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </div>
      <div style={{ marginTop: '5px', display: 'flex', gap: '5px', alignItems: 'baseline' }}>
        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: C.gold, whiteSpace: 'nowrap' }}>{num}</span>
        <span style={{ fontSize: '10.5px', color: C.ink, lineHeight: 1.25, overflowWrap: 'break-word', minWidth: 0 }}>{title}</span>
      </div>
      {meta && (
        <div style={{ fontSize: '9px', color: C.inkFaint, fontStyle: 'italic', lineHeight: 1.2, marginTop: '1px' }}>{meta}</div>
      )}
    </div>
  );
}

function PhotoCreditLine({ credit }) {
  if (!credit) return null;
  return (
    <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '2px', color: C.inkFaint, textTransform: 'uppercase', marginBottom: '12px' }}>
      TEMPORARY PHOTOS COURTESY OF{' '}
      <span style={{ color: C.gold, fontWeight: 700 }}>@{credit.handle}</span>
      {' '}FROM THIS{' '}
      <a href={credit.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 700, textDecoration: 'none' }}>SOURCE</a>
    </div>
  );
}

// Full-screen zoomed view of one gallery card, with prev/next paging so the
// whole set can be browsed without leaving the lightbox: side arrows, ←/→
// keys, trackpad horizontal scroll, and touch swipe all page; Esc or a click
// on the backdrop closes.
function Lightbox({ cards, index, photoUrlFor, onNavigate, onClose }) {
  const card = cards[index];
  const touchX = useRef(null);
  const wheelLock = useRef(0);
  const step = (dir) => onNavigate((index + dir + cards.length) % cards.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Sized as a thumb-friendly tap target (48px+ wide) with a heavy glyph and
  // gold border so it stays visible over bright card art on small screens.
  const arrowStyle = (side) => ({
    position: 'fixed',
    [side]: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '52px',
    height: '92px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.78)',
    border: `2px solid ${C.gold}`,
    borderRadius: '5px',
    color: C.gold,
    fontFamily: "'Orbitron', monospace",
    fontWeight: 900,
    fontSize: '30px',
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    boxShadow: '0 0 12px rgba(0,0,0,0.8)',
    WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
      }}
      onWheel={(e) => {
        // Trackpad horizontal scroll pages too; the cooldown keeps one
        // physical swipe (which fires many wheel events) to one step.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 12) return;
        const now = Date.now();
        if (now - wheelLock.current < 400) return;
        wheelLock.current = now;
        step(e.deltaX > 0 ? 1 : -1);
      }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: '20px', cursor: 'zoom-out', touchAction: 'none' }}>
      <img src={photoUrlFor(card.num)} alt={`Card ${card.num} — ${card.title}`} style={{ maxWidth: '100%', maxHeight: '82vh', borderRadius: '4px', border: `1px solid ${C.border}` }} />
      <div style={{ marginTop: '14px', textAlign: 'center' }}>
        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: C.gold, marginRight: '8px' }}>{card.num}</span>
        <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', letterSpacing: '1px', color: C.ink }}>{card.title}</span>
        {card.meta && <div style={{ fontSize: '11px', color: C.inkSoft, fontStyle: 'italic', marginTop: '4px' }}>{card.meta}</div>}
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, marginTop: '6px', letterSpacing: '1px' }}>
          {index + 1} / {cards.length}
        </div>
      </div>
      <button aria-label="Previous card" onClick={(e) => { e.stopPropagation(); step(-1); }} style={arrowStyle('left')}>❮</button>
      <button aria-label="Next card" onClick={(e) => { e.stopPropagation(); step(1); }} style={arrowStyle('right')}>❯</button>
    </div>
  );
}

function CardListGrid({ cards, photoUrlFor, photoCredit }) {
  const [viewIdx, setViewIdx] = useState(null); // index into cards | null
  if (!cards || cards.length === 0) return null;

  // Photo mode: the expanded list becomes a card-front gallery.
  if (photoUrlFor) {
    return (
      <div style={{
        padding: '16px 16px 18px',
        borderTop: `1px dashed ${C.grid}`,
        background: 'rgba(0, 0, 0, 0.18)',
      }}>
        <PhotoCreditLine credit={photoCredit} />
        <div className="ss-photo-grid">
          {cards.map((c, i) => (
            <GalleryCell
              key={i}
              url={photoUrlFor(c.num)}
              num={c.num}
              title={c.title}
              meta={c.meta}
              onView={() => setViewIdx(i)}
            />
          ))}
        </div>
        {viewIdx != null && (
          <Lightbox
            cards={cards}
            index={viewIdx}
            photoUrlFor={photoUrlFor}
            onNavigate={setViewIdx}
            onClose={() => setViewIdx(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 16px 8px',
      borderTop: `1px dashed ${C.grid}`,
      background: 'rgba(0, 0, 0, 0.18)',
    }}>
      <div className="ss-cards-grid">
        {cards.map((c, i) => (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(38px, auto) 1fr',
            gap: '0 8px',
            padding: '4px 0 5px',
            borderBottom: `1px dotted ${C.grid}`,
            alignItems: 'baseline',
          }}>
            <span style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '10.5px',
              color: C.gold,
              letterSpacing: '0.5px',
              gridColumn: 1,
              gridRow: '1 / span 2',
              alignSelf: 'start',
              paddingTop: '2px',
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}>
              {c.num}
            </span>
            <span style={{
              fontSize: '13px',
              color: C.ink,
              lineHeight: 1.3,
              gridColumn: 2,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}>
              {c.title}
            </span>
            {c.meta && (
              <span style={{
                display: 'block',
                fontSize: '11px',
                color: C.inkFaint,
                fontStyle: 'italic',
                lineHeight: 1.2,
                gridColumn: 2,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              }}>
                {c.meta}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ParallelChips({ list, accent }) {
  if (!list || list.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 6px', marginTop: '8px' }}>
      {list.map((p, i) => (
        <span key={i} style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: '10px',
          letterSpacing: '0.5px',
          color: C.inkSoft,
          background: 'rgba(var(--wash-rgb),0.03)',
          border: `1px solid ${C.grid}`,
          borderRadius: '2px',
          padding: '2px 7px',
          whiteSpace: 'nowrap',
        }}>
          {p.name} <span style={{ color: accent }}>{p.pr}</span>
        </span>
      ))}
    </div>
  );
}

function SubsetRow({ s, accent, expanded, onToggle }) {
  // Parallel-count display
  let paraDisplay;
  if (s.oneOfOne) {
    paraDisplay = <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: C.gold, letterSpacing: '1px' }}>1/1 ONLY</span>;
  } else if (s.noParallels) {
    paraDisplay = <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>NO PARALLELS</span>;
  } else {
    paraDisplay = (
      <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: accent, letterSpacing: '1px' }}>
        {s.parallels} PARALLELS
      </span>
    );
  }

  const hasCards = s.cards && s.cards.length > 0;

  return (
    <div style={{ borderTop: `1px solid ${C.grid}` }}>
      <div className="ss-subset-row" style={{ padding: '12px 14px' }}>
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontWeight: 800,
          fontSize: '20px',
          color: C.ink,
          lineHeight: 1,
        }}>
          {s.count}
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '8px', color: C.inkFaint, letterSpacing: '1px', marginTop: '4px' }}>
            CARDS
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: 700, fontSize: '12px', letterSpacing: '1.5px', color: C.ink, marginBottom: '4px' }}>
            {s.name}
          </div>
          <div style={{ fontSize: '12px', color: C.inkSoft, lineHeight: 1.4 }}>
            {s.meta}
            {s.odds && (
              <span style={{ color: C.inkFaint, fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', marginLeft: '8px' }}>
                · {s.odds}
              </span>
            )}
          </div>
          <ParallelChips list={s.parallelList} accent={accent} />
        </div>

        <div className="ss-paracount" style={{ textAlign: 'right' }}>
          {paraDisplay}
        </div>
      </div>

      {hasCards && (
        <div style={{ padding: '0 14px 12px' }}>
          <button
            type="button"
            className={s.photoUrlFor ? 'ss-cardtoggle-gold' : 'ss-cardtoggle'}
            onClick={onToggle}
            style={{
              background: 'transparent',
              border: `1px solid ${s.photoUrlFor ? C.gold : C.grid}`,
              color: s.photoUrlFor ? C.gold : C.inkSoft,
              fontFamily: "'Orbitron', monospace",
              fontWeight: s.photoUrlFor ? 700 : 600,
              fontSize: '10px',
              letterSpacing: '2px',
              padding: '8px 14px',
              cursor: 'pointer',
              borderRadius: '2px',
              transition: 'all 0.15s ease',
            }}
          >
            {expanded ? `▴ HIDE CARDS` : `▾ SHOW ALL ${s.cards.length} CARDS${s.photoUrlFor ? ' + PHOTOS' : ''}`}
          </button>
        </div>
      )}

      {hasCards && expanded && <CardListGrid cards={s.cards} photoUrlFor={s.photoUrlFor} photoCredit={s.photoCredit} />}
    </div>
  );
}

function GroupCard({ pillLabel, pillBg, pillTextColor, accent, summary, items, groupKey, expandedMap, toggle }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      background: 'rgba(var(--pcard-rgb),0.55)',
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${accent}`,
      marginBottom: '14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px 0', flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: "'Orbitron', monospace",
          fontWeight: 700,
          fontSize: '9px',
          letterSpacing: '2px',
          padding: '5px 10px 4px',
          background: pillBg,
          color: pillTextColor,
        }}>
          {pillLabel}
        </span>
        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
          {summary}
        </span>
      </div>
      <div style={{ marginTop: '10px' }}>
        {items.map((s, i) => {
          const key = `${groupKey}-${i}`;
          return (
            <SubsetRow
              key={key}
              s={s}
              accent={accent}
              expanded={!!expandedMap[key]}
              onToggle={() => toggle(key)}
            />
          );
        })}
      </div>
    </div>
  );
}

// --- Main component ---

export default function SetSummary({ setId }) {
  const [user, setUser] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [exportState, setExportState] = useState('idle'); // idle | busy | done | error
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-section parallel lists derived from the set's checklist data file,
  // keyed by the section's first card number (the stable join key between a
  // summary sub-set and its checklist section).
  const [sectionParallels, setSectionParallels] = useState(null);
  // Card totals computed from the same data file: { base, insert, auto, sketch }.
  const [cardCounts, setCardCounts] = useState(null);
  // CK 3.0: flag off = no sticky bar, bottom block keeps its two old buttons.
  const stickyBarEnabled = useFeatureFlag('sticky_track_bar');

  const toggle = (key) => {
    setExpandedMap(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Excel download: the whole workbook is built client-side from the summary
  // this page already rendered plus the checklist data file; exceljs stays out
  // of the main bundle until the first click.
  const runExport = async () => {
    if (exportState === 'busy') return;
    setExportState('busy');
    try {
      await exportSetSummaryXlsx({ setId, summary: data });
      setExportState('done');
      setTimeout(() => setExportState('idle'), 3000);
    } catch {
      setExportState('error');
      setTimeout(() => setExportState('idle'), 4000);
    }
  };

  // Registry entry drives the optional photo gallery (summaryGallery) and its
  // courtesy credit (photoCredit) — same config the Checklist header uses.
  const reg = SET_REGISTRY.find(s => s.id === setId);
  const gallery = reg?.summaryGallery;
  const gallerySections = gallery ? new Set(gallery.sections || []) : null;

  // Attach each sub-set's parallel list (from the checklist data file) so
  // rows can show names + print runs instead of a bare count — plus, when the
  // registry declares photo coverage for the matched section, a URL builder
  // that turns the expanded card list into a photo gallery.
  const withParallels = (items) => (items || []).map(s => {
    const first = s.cards?.[0]?.num;
    // Sections can share a first card number (2025 Chrome's base and sketch
    // sections both start at "1") — disambiguate by card count, falling back
    // to the sole match when the summary's count drifts from the checklist.
    const entries = first != null && sectionParallels ? sectionParallels[String(first)] : null;
    const match = entries && (entries.find(e => e.len === s.count) || (entries.length === 1 ? entries[0] : null));
    const out = { ...s };
    // A summary that explicitly claims no parallels wins over the checklist
    // file — never render chips that contradict the row's own badge.
    if (!s.noParallels && match?.list) out.parallelList = match.list;
    if (match?.sectionId && gallerySections?.has(match.sectionId)) {
      out.photoUrlFor = (num) => `${gallery.storageBase}/${setId}__${match.sectionId}__${num}__front`;
      out.photoCredit = reg.photoCredit || null;
    }
    return out;
  });

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  // Load the checklist data file so sub-set rows can show which parallels
  // (with print runs) each section carries, not just a count.
  useEffect(() => {
    let cancelled = false;
    async function loadSectionParallels() {
      const reg = SET_REGISTRY.find(s => s.id === setId);
      if (!reg?.dataFile) return;
      try {
        const module = await import(`./sets/${reg.dataFile}.js`);
        if (cancelled) return;
        const fmt = p => ({
          name: (p.name || 'Base').replace(/ Refractor$/, ''),
          pr: p.print_run == null ? '∞' : p.print_run === 1 ? '1/1' : `/${p.print_run}`,
        });
        // Same per-type fallbacks the Checklist renderer uses when a section
        // has no inline parallels array.
        const fallback = {
          base: module.PARALLELS,
          insert: module.INSERT_PARALLELS || module.PARALLELS,
          auto: module.AUTO_PARALLELS || module.PARALLELS,
        };
        const byFirstCard = {};
        const counts = { base: 0, insert: 0, auto: 0, sketch: 0 };
        for (const section of module.SETS || []) {
          if (section.type in counts) counts[section.type] += section.cards?.length || 0;
          const first = section.cards?.[0]?.n;
          const list = section.parallels ?? fallback[section.type] ?? null;
          if (first != null) {
            // sectionId rides along so the summary row can also join to the
            // set's photo gallery (summaryGallery in the registry), which is
            // keyed by checklist section id — even for parallel-less sections.
            (byFirstCard[String(first)] ||= []).push({
              len: section.cards.length,
              sectionId: section.id,
              list: list?.length ? list.map(fmt) : null,
            });
          }
        }
        setSectionParallels(byFirstCard);
        setCardCounts(counts);
      } catch {
        // No data file for this set — rows keep showing the count only.
      }
    }
    loadSectionParallels();
    return () => { cancelled = true; };
  }, [setId]);

  // Fetch the summary: try Supabase first, fall back to JS file.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function fetchSummary() {
      try {
        // ?preview=draft lets admins preview unpublished drafts.
        const params = new URLSearchParams(window.location.search);
        const previewMode = params.get('preview') === 'draft';

        let isAdmin = false;
        if (previewMode) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('is_admin')
              .eq('id', user.id)
              .single();
            isAdmin = !!profile?.is_admin;
          }
        }

        let query = supabase.from('set_summaries').select('*').eq('set_id', setId);
        if (!(previewMode && isAdmin)) {
          query = query.eq('status', 'published');
        }
        const { data: row, error } = await query.maybeSingle();

        if (cancelled) return;

        if (!error && row) {
          setData(rowToSummary(row));
        } else {
          // No row in Supabase (or fetch failed) — fall back to local JS file.
          setData(SET_SUMMARIES[setId] || null);
        }
      } catch (err) {
        if (cancelled) return;
        // Network error or similar — fall back to local JS file.
        setData(SET_SUMMARIES[setId] || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchSummary();
    return () => { cancelled = true; };
  }, [setId]);

  // Set <title> and scroll to top on mount / setId change
  useEffect(() => {
    if (data) {
      document.title = `${data.year} ${data.brand} ${data.title} · CollectionKeeper`;
    }
    window.scrollTo(0, 0);
    setExpandedMap({}); // collapse everything when navigating to a different set
  }, [setId, data]);

  // Loading state
  if (loading) {
    return (
      <div className="ss-page">
        <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
        {user && <NavDrawer user={user} />}
        <div className="ss-wrap" style={{ textAlign: 'center', paddingTop: '120px' }}>
          <div style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: '11px',
            color: C.cyan,
            letterSpacing: '4px',
          }}>
            // LOADING SET SUMMARY
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ss-page">
        <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
        {user && <NavDrawer user={user} />}
        <div className="ss-wrap">
          <a href="/resources" style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: C.inkFaint, letterSpacing: '3px', textDecoration: 'none', display: 'inline-block', marginBottom: '24px' }}>
            ← BACK TO RESOURCES
          </a>
          <h1 style={{ fontFamily: "'Orbitron', monospace", fontSize: '24px', color: C.ink, marginBottom: '12px' }}>SET NOT FOUND</h1>
          <p style={{ color: C.inkSoft }}>
            We don't have a summary page for "{setId}" yet. Head back to <a href="/resources" style={{ color: C.cyan }}>Resources</a> to see what's available.
          </p>
        </div>
      </div>
    );
  }

  const communityUrl = `/community/${data.setId}`;
  const galleryHomeUrl = `/`;

  // Header shortcut: expand every base sub-set and jump to the section, so
  // the card list + photo gallery is one tap from the top of the page.
  const baseHasPhotos = withParallels(data.subsets?.base).some(s => s.photoUrlFor);
  const showBaseCards = () => {
    setExpandedMap(prev => {
      const next = { ...prev };
      (data.subsets?.base || []).forEach((s, i) => {
        if (s.cards?.length) next[`base-${i}`] = true;
      });
      return next;
    });
    // Jump (not smooth-scroll) after the expanded grid commits — the 100-cell
    // render cancels an in-flight smooth scroll animation.
    setTimeout(() => {
      document.getElementById('ss-base-group')?.scrollIntoView({ block: 'start' });
    }, 100);
  };

  return (
    <div className={'ss-page' + (stickyBarEnabled ? ' ss-has-bar' : '')}>
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      {user && <NavDrawer user={user} />}

      {/* === CK 3.0 STICKY "MARK WHAT I OWN" BAR === */}
      {stickyBarEnabled && (
        <div className="ss-sticky-bar">
          <div className="ss-sticky-inner">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: C.inkSoft }}>
                TRACKING
              </div>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', fontWeight: 700, color: C.ink, letterSpacing: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {(reg?.shortName || `${data.year} ${data.title}`).toUpperCase()}
              </div>
            </div>
            <MarkWhatIOwnButton setId={data.setId} user={user} compact />
          </div>
        </div>
      )}

      <div className="ss-wrap">

        {/* === HEADER === */}
        <a href="/resources" style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: '9px',
          color: C.inkFaint,
          letterSpacing: '3px',
          textDecoration: 'none',
          display: 'inline-block',
          marginBottom: '24px',
        }}>
          ← BACK TO RESOURCES
        </a>

        <div className="ss-clear-nav" style={{ paddingBottom: '24px', borderBottom: `1px solid ${C.grid}`, marginBottom: '32px' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: C.cyan, letterSpacing: '4px', marginBottom: '12px' }}>
            // SET BREAKDOWN · COLLECTOR REFERENCE
          </div>
          <h1 className="ss-hero-title" style={{
            fontFamily: "'Orbitron', monospace",
            fontWeight: 900,
            fontSize: '44px',
            lineHeight: 0.95,
            letterSpacing: '-0.5px',
            color: C.ink,
            marginBottom: '8px',
          }}>
            {data.year} {data.brand}<br />
            <span style={{ color: C.gold }}>{data.title}</span>
          </h1>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', letterSpacing: '3px', color: C.inkSoft, marginBottom: '14px' }}>
            {(() => {
              // "8 / 12 / 2026" — future dates read RELEASES until the day arrives.
              const d = new Date(String(data.releaseDate).replace(/\s/g, ''));
              return !isNaN(d) && d > new Date() ? 'RELEASES' : 'RELEASED';
            })()} · {data.releaseDate}
          </div>
          <p style={{ fontSize: '15px', lineHeight: 1.55, color: C.inkSoft, maxWidth: '720px' }}>
            {data.lede}
          </p>
          {data.subsets?.base?.some(s => s.cards?.length) && (
            <button
              type="button"
              className="ss-cardtoggle-gold"
              onClick={showBaseCards}
              style={{
                marginTop: '16px',
                background: 'transparent',
                border: `1px solid ${C.gold}`,
                color: C.gold,
                fontFamily: "'Orbitron', monospace",
                fontWeight: 700,
                fontSize: '10px',
                letterSpacing: '2px',
                padding: '10px 18px',
                cursor: 'pointer',
                borderRadius: '2px',
                transition: 'all 0.15s ease',
              }}
            >
              {`▾ SHOW BASE CARDS${baseHasPhotos ? ' + PHOTOS' : ''}`}
            </button>
          )}
        </div>

        {/* === STATS GRID === */}
        <div className="ss-stats" style={{ marginBottom: cardCounts ? '10px' : '32px' }}>
          {data.stats.map((s, i) => <Stat key={i} stat={s} />)}
        </div>

        {/* === CHECKLIST TOTAL (computed from the set's data file) === */}
        {cardCounts && (cardCounts.base + cardCounts.insert + cardCounts.auto) > 0 && (
          <div style={{
            background: 'rgba(var(--pcard-rgb),0.55)',
            border: `1px solid ${C.grid}`,
            padding: '12px 16px',
            marginBottom: '32px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: '8px 18px',
          }}>
            <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: C.gold }}>
              FULL CHECKLIST:
            </span>
            <span style={{ fontFamily: "'Orbitron', monospace", fontWeight: 800, fontSize: '18px', color: C.cyan, lineHeight: 1 }}>
              {(cardCounts.base + cardCounts.insert + cardCounts.auto).toLocaleString()} cards
            </span>
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: C.inkSoft }}>
              {[
                cardCounts.base > 0 && `${cardCounts.base} base`,
                cardCounts.insert > 0 && `${cardCounts.insert} inserts`,
                cardCounts.auto > 0 && `${cardCounts.auto} autographs`,
              ].filter(Boolean).join(' · ')}
              {cardCounts.sketch > 0 ? ` (+ ${cardCounts.sketch} sketch subjects)` : ''}
            </span>
          </div>
        )}

        {/* === HOBBY BOX + PULL RATES === */}
        <div className="ss-twocol" style={{ marginBottom: '32px' }}>

          {/* Hobby box */}
          <div style={{ background: 'rgba(var(--pcard-rgb),0.55)', border: `1px solid ${C.grid}`, padding: '16px 18px' }}>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', fontWeight: 700, letterSpacing: '2.5px', color: C.gold, marginBottom: '14px' }}>
              // HOBBY BOX CONFIGURATION
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
              {data.hobbyBox.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                  <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: 800, fontSize: '24px', color: C.cyan, minWidth: '32px', lineHeight: 1 }}>
                    {b.v}
                  </div>
                  <div style={{ fontSize: '12px', color: C.inkSoft, lineHeight: 1.3 }}>
                    {b.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pull rates */}
          <div style={{ background: 'rgba(var(--pcard-rgb),0.55)', border: `1px solid ${C.grid}`, padding: '16px 18px' }}>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', fontWeight: 700, letterSpacing: '2.5px', color: C.gold, marginBottom: '14px' }}>
              // INSERT PULL RATES
            </div>
            <div>
              {data.pullRates.map((r, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  padding: '8px 0',
                  borderBottom: i === data.pullRates.length - 1 ? 'none' : `1px dotted ${C.grid}`,
                }}>
                  <div style={{ fontSize: '12.5px', color: C.ink }}>{r.name}</div>
                  <div style={{
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: TIER_COLOR[r.tier] || C.ink,
                    whiteSpace: 'nowrap',
                  }}>
                    {r.odds}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* === PARALLEL RAINBOW === */}
        <div style={{ marginBottom: '32px' }}>
          <SectionHeader
            tickColor={C.gold}
            label={`${data.subsets?.base?.length ? 'BASE SET' : 'AUTOGRAPHS'} — THE PARALLEL RAINBOW`}
            meta={`${data.parallelRainbow.length} PARALLELS · ${data.parallelRainbow[0].name} → ${data.parallelRainbow[data.parallelRainbow.length - 1].name} ${data.parallelRainbow[data.parallelRainbow.length - 1].pr}`}
          />
          <div style={{
            background: 'rgba(var(--pcard-rgb),0.55)',
            border: `1px solid ${C.grid}`,
            padding: '16px',
          }}>
            <div className="ss-rainbow">
              {data.parallelRainbow.map((p, i) => <ParallelChip key={i} p={p} />)}
            </div>
          </div>
        </div>

        {/* === ARTISTS === */}
        {data.artists && data.artists.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <SectionHeader tickColor={C.cyan} label="COMMISSIONED ARTISTS" meta="INSERT SETS" />
            <div style={{
              background: 'rgba(var(--pcard-rgb),0.55)',
              border: `1px solid ${C.grid}`,
              padding: '18px 20px',
            }}>
              <div className="ss-artists">
                {data.artists.map((a, i) => (
                  <div key={i} style={{ fontSize: '13px', color: C.inkSoft, lineHeight: 1.5 }}>
                    <strong style={{ color: C.gold, fontWeight: 600 }}>{a.name}</strong>
                    <span style={{ color: C.inkFaint }}> · </span>
                    {a.work}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* === SUB-SET BREAKDOWN === */}
        <div style={{ marginBottom: '32px' }}>
          <SectionHeader tickColor={C.gold} label="EVERY SUB-SET, MAPPED" meta="TAP A SUB-SET TO SEE EVERY CARD" />

          {data.subsets.base?.length > 0 && (
          <div id="ss-base-group">
          <GroupCard
            pillLabel="BASE"
            pillBg={C.ink}
            pillTextColor={C.uiBg}
            accent={C.ink}
            summary={`${data.subsets.base.length} SUB-SET · ${data.subsets.base.reduce((sum, s) => sum + s.count, 0)} CARDS`}
            items={withParallels(data.subsets.base)}
            groupKey="base"
            expandedMap={expandedMap}
            toggle={toggle}
          />
          </div>
          )}

          {data.subsets.insert?.length > 0 && (
          <GroupCard
            pillLabel="INSERT"
            pillBg={C.cyan}
            pillTextColor={C.uiBg}
            accent={C.cyan}
            summary={`${data.subsets.insert.length} SUB-SETS · ${data.subsets.insert.reduce((sum, s) => sum + s.count, 0)} CARDS`}
            items={withParallels(data.subsets.insert)}
            groupKey="insert"
            expandedMap={expandedMap}
            toggle={toggle}
          />
          )}

          <GroupCard
            pillLabel="AUTO"
            pillBg={C.gold}
            pillTextColor={C.uiBg}
            accent={C.gold}
            summary={`${data.subsets.auto.length} SUB-SETS · ${data.subsets.auto.reduce((sum, s) => sum + s.count, 0)} CARDS${(() => {
              // Per-set guarantee lives in the hobby box data ("1 Autograph per
              // box", "2 Autographs per case") — never assert a hardcoded rate.
              const g = (data.hobbyBox || []).find(r => /autograph/i.test(r.l));
              return g ? ` · ${g.v} ${g.l.toUpperCase()}` : '';
            })()}`}
            items={withParallels(data.subsets.auto)}
            groupKey="auto"
            expandedMap={expandedMap}
            toggle={toggle}
          />

          {data.subsets.sketch && (
            <GroupCard
              pillLabel="SKETCH"
              pillBg={C.purple}
              pillTextColor={C.ink}
              accent={C.purple}
              summary={`${data.subsets.sketch.length} ${data.subsets.sketch.length === 1 ? 'SUB-SET' : 'SUB-SETS'} · ${data.subsets.sketch.reduce((sum, s) => sum + s.count, 0)} CARDS · ALL 1/1`}
              items={withParallels(data.subsets.sketch)}
              groupKey="sketch"
              expandedMap={expandedMap}
              toggle={toggle}
            />
          )}
        </div>

        {/* === DOWNLOAD + SUGGEST EDIT BUTTONS === */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={runExport}
            style={{
              background: 'transparent',
              border: `1px solid ${
                exportState === 'busy' ? C.grid
                : exportState === 'done' ? C.green
                : exportState === 'error' ? C.red
                : C.gold}`,
              color:
                exportState === 'busy' ? C.inkFaint
                : exportState === 'done' ? C.green
                : exportState === 'error' ? C.red
                : C.gold,
              fontFamily: "'Orbitron', monospace",
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '2px',
              padding: '10px 18px',
              cursor: exportState === 'busy' ? 'wait' : 'pointer',
              borderRadius: '2px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (exportState !== 'idle') return;
              e.currentTarget.style.background = C.gold;
              e.currentTarget.style.color = C.uiBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              if (exportState === 'idle') e.currentTarget.style.color = C.gold;
            }}
          >
            {exportState === 'busy' ? '◐ BUILDING WORKBOOK…'
              : exportState === 'done' ? '✓ DOWNLOADED'
              : exportState === 'error' ? '✕ EXPORT FAILED — RETRY'
              : '⬇ DOWNLOAD EXCEL (.XLSX)'}
          </button>
          <button
            type="button"
            onClick={() => setEditModalOpen(true)}
            style={{
              background: 'transparent',
              border: `1px solid ${C.grid}`,
              color: C.inkSoft,
              fontFamily: "'Orbitron', monospace",
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '2px',
              padding: '10px 18px',
              cursor: 'pointer',
              borderRadius: '2px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = C.cyan;
              e.currentTarget.style.color = C.cyan;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.grid;
              e.currentTarget.style.color = C.inkSoft;
            }}
          >
            ⚐ SUGGEST AN EDIT TO THIS SET
          </button>
        </div>

        {/* === FULL CHECKLIST CTA === */}
        <div style={{
          background: `linear-gradient(135deg, rgba(var(--cyan-rgb),0.08) 0%, rgba(var(--gold-rgb),0.06) 100%)`,
          border: `1px solid ${C.cyan}`,
          padding: '28px 24px',
          textAlign: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', color: C.cyan, letterSpacing: '4px', marginBottom: '10px' }}>
            // READY TO TRACK
          </div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '22px', fontWeight: 800, color: C.ink, letterSpacing: '1px', marginBottom: '8px' }}>
            START TRACKING THIS SET
          </div>
          <p style={{ fontSize: '13px', color: C.inkSoft, marginBottom: '20px', maxWidth: '480px', margin: '0 auto 20px' }}>
            Mark what you own, build your wishlist, and trade with collectors worldwide on the Community Checklist.
          </p>
          {stickyBarEnabled ? (
            <div style={{ maxWidth: '480px', margin: '0 auto' }}>
              <MarkWhatIOwnButton setId={data.setId} user={user} />
            </div>
          ) : (
          <div className="ss-cta-grid" style={{ maxWidth: '480px', margin: '0 auto' }}>
            <a href={communityUrl} style={{
              background: C.cyan,
              color: C.uiBg,
              padding: '14px 20px',
              fontFamily: "'Orbitron', monospace",
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '2px',
              textDecoration: 'none',
              borderRadius: '3px',
              display: 'inline-block',
            }}>
              OPEN COMMUNITY CHECKLIST →
            </a>
            <a href={galleryHomeUrl} style={{
              background: 'transparent',
              color: C.gold,
              border: `1px solid ${C.gold}`,
              padding: '13px 20px',
              fontFamily: "'Orbitron', monospace",
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '2px',
              textDecoration: 'none',
              borderRadius: '3px',
              display: 'inline-block',
            }}>
              MY CHECKLISTS
            </a>
          </div>
          )}
        </div>

        {/* === FOOTER === */}
        <div style={{
          paddingTop: '20px',
          borderTop: `1px solid ${C.grid}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: C.cyan, letterSpacing: '2px' }}>
            COLLECTIONKEEPER · TRACK · SHARE · TRADE
          </div>
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
            // .ORG BECAUSE IT WILL ALWAYS BE FREE
          </div>
        </div>

      </div>

      <SuggestEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        user={user}
        context={`${data.year} ${data.brand} ${data.title}`}
      />
    </div>
  );
}
